import React, { useState, useCallback } from 'react';
import { Search, ChartBar, Sparkles } from 'lucide-react';

// Mouse Light Effect Component
const MouseLight = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  return (
    <div 
      className="absolute inset-0 overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <div
        className="pointer-events-none absolute -inset-px transition-all duration-200 ease-out"
        style={{
          background: `radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, 
            rgba(139, 92, 246, 0.07),
            rgba(99, 102, 241, 0.07),
            transparent 40%)`
        }}
      />
    </div>
  );
};

// Enhanced Dark Background Pattern
const DarkBackground = () => (
  <div className="absolute inset-0 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-[#1a1437] to-gray-900" />
    
    <div className="absolute inset-0">
      {[...Array(15)].map((_, i) => (
        <div
          key={i}
          className={`absolute rounded-full blur-2xl
            ${i % 3 === 0 ? 'bg-purple-400 w-64 h-64 opacity-20' : 
              i % 3 === 1 ? 'bg-blue-400 w-96 h-96 opacity-15' : 
              'bg-violet-400 w-72 h-72 opacity-20'}
            animate-float-slow`}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${15 + Math.random() * 10}s`
          }}
        />
      ))}
    </div>

    <div className="absolute inset-0 opacity-[0.07]" 
         style={{ 
           backgroundImage: `
             radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 70%),
             linear-gradient(to right, #fff 1px, transparent 1px), 
             linear-gradient(to bottom, #fff 1px, transparent 1px)
           `,
           backgroundSize: '100% 100%, 50px 50px, 50px 50px'
         }} 
    />

    <div className="absolute inset-0">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="absolute h-[1px] w-1/4 bg-gradient-to-r from-transparent via-purple-400/30 to-transparent
            animate-pulse-slow"
          style={{
            left: `${20 + i * 20}%`,
            top: `${30 + i * 20}%`,
            transform: `rotate(${-5 + i * 5}deg)`,
            animationDelay: `${i * 0.5}s`
          }}
        />
      ))}
    </div>
  </div>
);

const SearchBar = ({ value, onChange, placeholder = "Search alumni by name, skills, company..." }) => (
  <div className="relative group w-full max-w-3xl mx-auto">
    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-400 to-blue-400 rounded-2xl opacity-50 group-hover:opacity-100 blur transition-all duration-300" />
    <div className="relative flex items-center bg-white/95 rounded-2xl shadow-lg">
      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
        <Search className="h-5 w-5 text-purple-500" />
      </div>
      <input
        type="text"
        className="w-full pl-12 pr-12 py-4 bg-transparent rounded-2xl text-gray-900 
          placeholder-gray-500 text-md focus:outline-none"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
        <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-purple-500 bg-purple-50 rounded">
          ⌘K
        </kbd>
      </div>
    </div>
  </div>
);

const Stat = ({ value, label }) => (
  <div className="text-center px-8">
    <div className="text-5xl font-bold text-white mb-1">
      {value}
    </div>
    <div className="text-purple-200/90 text-md">
      {label}
    </div>
  </div>
);

const HeroSection = ({
  title = "Alumni Directory",
  subtitle = "Connect with our growing community of alumni across various cities",
  showSearch = true,
  searchConfig = {},
  statsConfig = [
    { value: "5", label: "Alumni" },
    { value: "4", label: "Cities" },
    { value: "7", label: "Departments" }
  ],
  analyticsComponent
}) => {
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);

  return (
    <div className="relative w-full">
      {/* Changed to h-[70vh] for 80% viewport height */}
      <div className="relative overflow-hidden h-[70vh]">
        <DarkBackground />
        <MouseLight />
        
        {/* Adjusted padding for 70vh container */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
          <div className="text-center space-y-6 md:space-y-8">
            <div 
              className={`inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm 
                border border-white/20 text-white text-sm hover:scale-105 transition-all duration-300
                ${analyticsComponent ? 'cursor-pointer hover:bg-white/20' : ''}`}
              onClick={() => analyticsComponent && setIsAnalyticsOpen(true)}
            >
              {analyticsComponent ? (
                <>
                  <ChartBar className="h-4 w-4 mr-2 text-purple-300" />
                  <span>View Insights</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2 text-purple-300" />
                  <span>Discover what's new</span>
                </>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-200 to-blue-200">
                {title.split(' ')[0]}
              </span>
              <span className="text-white ml-4">
                {title.split(' ').slice(1).join(' ')}
              </span>
            </h1>
            
            {subtitle && (
              <p className="text-lg md:text-xl text-purple-100/80 max-w-3xl mx-auto font-light">
                {subtitle}
              </p>
            )}

            {statsConfig.length > 0 && (
              <div className="flex flex-wrap justify-center gap-4 md:gap-16 my-6">
                {statsConfig.map((stat, index) => (
                  <Stat key={index} {...stat} />
                ))}
              </div>
            )}

            {showSearch && (
              <div className="mt-6">
                <SearchBar
                  value={searchConfig.value}
                  onChange={searchConfig.onChange}
                  placeholder={searchConfig.placeholder}
                />
              </div>
            )}
          </div>
        </div>

        {/* Adjusted wave position */}
        <div className="absolute bottom-0 inset-x-0">
          <svg 
            viewBox="0 0 1440 74" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto"
            preserveAspectRatio="none"
          >
            <path 
              d="M0 24C480 74 960 74 1440 24V74H0V24Z" 
              className="fill-gray-50"
            />
          </svg>
        </div>

        {analyticsComponent && React.cloneElement(analyticsComponent, {
          isOpen: isAnalyticsOpen,
          onClose: () => setIsAnalyticsOpen(false)
        })}
      </div>
    </div>
  );
};

const styles = `
  @keyframes float-slow {
    0%, 100% { transform: translateY(0) rotate(0); }
    50% { transform: translateY(-20px) rotate(5deg); }
  }

  .animate-float-slow {
    animation: float-slow 20s ease-in-out infinite;
  }

  @keyframes pulse-slow {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 0.4; }
  }

  .animate-pulse-slow {
    animation: pulse-slow 4s ease-in-out infinite;
  }
`;

export default HeroSection;